import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type { InviteResult } from '@opti-sea/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAssignments, useNames, type Assignment } from '../modules/risq/data';
import { PageTitle, QueryState, usePositionNames } from '../ui/components';
import { formatDate } from '../ui/format';
import { useWorkplace } from './access';
import { InviteError, useInvite, useRevokeAssignment } from './accessActions';
import { useInviteText } from './inviteText';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * D8 Crew & access: position → person, since; give access (invite, hands over a taken position
 * after a confirmation, D14); end access; assignment history.
 */
export function CrewPage() {
  const { t } = useTranslation('dashboard');
  const { vesselId = '' } = useParams();
  const { vessels, access, positions } = useWorkplace();
  const vessel = vessels.find((v) => v.id === vesselId);
  const assignments = useAssignments(vessel === undefined ? [] : [vessel.id]);
  const names = useNames(vesselId);
  const { name } = usePositionNames();
  const invite = useInvite();
  const revoke = useRevokeAssignment();
  const inviteText = useInviteText();
  const [form, setForm] = useState({ positionCode: '', fullName: '', email: '' });
  const [replacing, setReplacing] = useState(false);
  const [reason, setReason] = useState('');
  const [ending, setEnding] = useState<Assignment | null>(null);
  const [done, setDone] = useState<{ text: string; result?: InviteResult; email?: string } | null>(
    null,
  );

  if (vessel === undefined) {
    return <Text>{t('vessel.notFound')}</Text>;
  }
  const rows = assignments.data?.get(vessel.id) ?? [];
  const active = rows.filter((a) => a.revokedAt === null);
  const person = (id: string) => names.data?.get(id) ?? t('activity.someone');
  const holderOf = (code: string) => active.find((a) => a.positionCode === code);
  const holder = form.positionCode === '' ? undefined : holderOf(form.positionCode);
  const emailOk = EMAIL.test(form.email.trim());
  const ready = form.positionCode !== '' && form.fullName.trim().length >= 2 && emailOk;

  const give = (replace: boolean) => {
    const request = {
      kind: 'crew' as const,
      email: form.email.trim(),
      fullName: form.fullName.trim(),
      vesselId: vessel.id,
      positionCode: form.positionCode,
      replace,
      reason: reason.trim() === '' ? null : reason.trim(),
    };
    setDone(null);
    invite.mutate(request, {
      onSuccess: (result) => {
        setReplacing(false);
        setReason('');
        setDone({
          text: t('crew.given', {
            name: request.fullName,
            position: name(request.positionCode),
            vessel: vessel.name,
          }),
          result,
          email: request.email.toLowerCase(),
        });
        setForm({ positionCode: '', fullName: '', email: '' });
      },
      onError: (error) => {
        if (error instanceof InviteError && error.code === 'position_taken') {
          setReplacing(true);
        }
      },
    });
  };

  const inviteError =
    invite.error instanceof InviteError && invite.error.code !== 'position_taken'
      ? t(`invite.errors.${invite.error.code}`)
      : invite.error !== null && !(invite.error instanceof InviteError)
        ? t('common.saveFailed', { message: invite.error.message })
        : null;

  return (
    <>
      <PageTitle
        title={t('crew.title', { vessel: vessel.name })}
        back={{ to: `/vessels/${vessel.id}`, label: vessel.name }}
      />
      <Stack gap="lg">
        {done === null ? null : (
          <Alert color="green" role="status" title={done.text}>
            {done.result === undefined || done.email === undefined
              ? null
              : inviteText(done.result, done.email)}
          </Alert>
        )}
        <QueryState loading={assignments.isPending} error={assignments.error}>
          {() => (
            <>
              <Card withBorder>
                <Stack>
                  <Title order={2} size="h3">
                    {t('crew.onBoard')}
                  </Title>
                  <Table>
                    <Table.Tbody>
                      {access.positions
                        .filter((p) => positions.includes(p) || holderOf(p.code) !== undefined)
                        .map((p) => {
                          const current = holderOf(p.code);
                          return (
                            <Table.Tr key={p.code}>
                              <Table.Td>{name(p.code)}</Table.Td>
                              <Table.Td>
                                {current === undefined ? (
                                  <Badge color="gray" variant="outline" tt="none">
                                    {t('crew.nobody')}
                                  </Badge>
                                ) : (
                                  <Text size="sm">
                                    {t('crew.since', {
                                      name: person(current.userId),
                                      date: formatDate(current.startsAt),
                                    })}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {current === undefined ? null : (
                                  <Button
                                    size="xs"
                                    variant="default"
                                    color="red"
                                    aria-label={`${t('crew.endAccess')}: ${person(current.userId)}`}
                                    onClick={() => {
                                      setReason('');
                                      setEnding(current);
                                    }}
                                  >
                                    {t('crew.endAccess')}
                                  </Button>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>

              <Card withBorder>
                <Stack>
                  <Title order={2} size="h3">
                    {t('crew.giveTitle')}
                  </Title>
                  <Text size="sm">{t('crew.giveBody')}</Text>
                  <Group align="flex-start">
                    <Select
                      label={t('crew.position')}
                      data={positions.map((p) => ({ value: p.code, label: name(p.code) }))}
                      value={form.positionCode === '' ? null : form.positionCode}
                      onChange={(value) => setForm({ ...form, positionCode: value ?? '' })}
                      description={
                        holder === undefined
                          ? undefined
                          : t('crew.takenNow', { name: person(holder.userId) })
                      }
                    />
                    <TextInput
                      label={t('crew.fullName')}
                      value={form.fullName}
                      maxLength={120}
                      onChange={(e) => setForm({ ...form, fullName: e.currentTarget.value })}
                    />
                    <TextInput
                      label={t('crew.email')}
                      type="email"
                      value={form.email}
                      maxLength={254}
                      error={form.email !== '' && !emailOk ? t('crew.emailInvalid') : undefined}
                      onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
                    />
                  </Group>
                  <Group>
                    <Button
                      disabled={!ready}
                      loading={invite.isPending && !replacing}
                      onClick={() => (holder === undefined ? give(false) : setReplacing(true))}
                    >
                      {t('crew.give')}
                    </Button>
                  </Group>
                  {inviteError === null ? null : (
                    <Alert color="red" role="alert">
                      {inviteError}
                    </Alert>
                  )}
                </Stack>
              </Card>

              <Card withBorder>
                <Stack>
                  <Title order={2} size="h3">
                    {t('crew.history')}
                  </Title>
                  <Table>
                    <Table.Tbody>
                      {rows.map((a) => (
                        <Table.Tr key={a.id}>
                          <Table.Td>{name(a.positionCode)}</Table.Td>
                          <Table.Td>{person(a.userId)}</Table.Td>
                          <Table.Td>
                            {a.revokedAt === null
                              ? t('crew.activeSince', { date: formatDate(a.startsAt) })
                              : t('crew.ended', {
                                  from: formatDate(a.startsAt),
                                  to: formatDate(a.revokedAt),
                                  reason: a.revokeReason ?? t('crew.noReason'),
                                })}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>
            </>
          )}
        </QueryState>
      </Stack>

      <Modal
        opened={replacing}
        onClose={() => setReplacing(false)}
        title={t('crew.replaceTitle', { position: name(form.positionCode) })}
      >
        <Stack>
          <Text>
            {holder === undefined
              ? t('crew.replaceBodyPerson', {
                  vessel: vessel.name,
                  position: name(form.positionCode),
                })
              : t('crew.replaceBody', {
                  name: person(holder.userId),
                  vessel: vessel.name,
                  newName: form.fullName.trim(),
                })}
          </Text>
          <TextInput
            label={t('crew.reason')}
            placeholder={t('crew.reasonHint')}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setReplacing(false)}>
              {t('common.cancel')}
            </Button>
            <Button color="red" loading={invite.isPending} onClick={() => give(true)}>
              {t('crew.replace')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={ending !== null}
        onClose={() => setEnding(null)}
        title={ending === null ? '' : t('crew.endTitle', { name: person(ending.userId) })}
      >
        {ending === null ? null : (
          <Stack>
            <Text>
              {t('crew.endBody', {
                name: person(ending.userId),
                position: name(ending.positionCode),
                vessel: vessel.name,
              })}
            </Text>
            <TextInput
              label={t('crew.reason')}
              placeholder={t('crew.reasonHint')}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            {revoke.error === null ? null : (
              <Alert color="red" role="alert">
                {t('common.saveFailed', { message: revoke.error.message })}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEnding(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                color="red"
                loading={revoke.isPending}
                onClick={() =>
                  revoke.mutate(
                    {
                      assignmentId: ending.id,
                      reason: reason.trim() === '' ? null : reason.trim(),
                    },
                    {
                      onSuccess: () => {
                        setDone({ text: t('crew.endedNow', { name: person(ending.userId) }) });
                        setEnding(null);
                        setReason('');
                      },
                    },
                  )
                }
              >
                {t('crew.endAccess')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
